// Typed wrapper for `/api/v1/calendar` — unified upcoming-releases
// feed across movies / albums / episodes (issue #9).
//
// Mirrors `crates/api/src/calendar.rs::CalendarEvent`. Renaming a
// field there means renaming it here too.

import { api } from "@/lib/api";

export type CalendarKind = "movie" | "album" | "episode";

/** Sub-type of the date — drives both the column-coloured chip and the
 *  grouping hint shown next to each row. */
export type CalendarDateType =
  | "theatrical"
  | "digital"
  | "physical"
  | "release"
  | "air";

export interface CalendarEvent {
  /** Discriminator for the row's link target and chip colour. */
  kind: CalendarKind;
  /** ISO `YYYY-MM-DD`. */
  date: string;
  date_type: CalendarDateType;
  /** Source-table row id — `movies.id` / `albums.id` / `episodes.id`. */
  media_id: number;
  /** Pre-formatted display string. */
  title: string;
  poster_url: string | null;
  /** Set for episode events so the row can deep-link to /tv/:series_id. */
  series_id: number | null;
  season_number: number | null;
  episode_number: number | null;
}

export interface CalendarRange {
  /** Inclusive ISO `YYYY-MM-DD`. Defaults server-side to today. */
  from?: string;
  /** Inclusive ISO `YYYY-MM-DD`. Defaults server-side to today + 60 days. */
  to?: string;
}

export const calendarApi = {
  /** Fetch the unified calendar feed. Server caps response at 1000 rows. */
  list: (range?: CalendarRange) => {
    const qs = new URLSearchParams();
    if (range?.from) qs.set("from", range.from);
    if (range?.to) qs.set("to", range.to);
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return api<CalendarEvent[]>(`/api/v1/calendar${suffix}`);
  },
};
